import MemberItem from '@/components/member-Item';
//import { groupsApis } from '@/configs/apis/groups/index';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { GroupPosition } from '@/configs/apis/groups/interface';
import type { User } from '@/configs/apis/user';
import { getAvatar } from '@/scripts/utils/genAvatar';
import styles from './index.module.scss';
import DeleteIcon from './images/delete.svg?react';

interface GroupRemoveMembersProps {
  open: boolean;
  normalMemberList: ChatGroupMember[];
  user: User | null;
  onCancel: () => void;
  onActionOk?: (selectUserIds: string[]) => Promise<any>;
}
const GroupRemoveMembers = ({
  open,
  normalMemberList,
  user,
  onCancel,
  onActionOk,
}: GroupRemoveMembersProps) => {
  const [loading, setLoading] = useState(false);
  const [selectUserIds, setSelectUserIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  const groupOwner = useMemo(() => {
    return normalMemberList?.find((item) => item.role === GroupPosition.Owner);
  }, [normalMemberList]);

  const isGroupOwner = useMemo(() => {
    return groupOwner?.user_id === user?.userId;
  }, [groupOwner, user?.userId]);

  const removeMemberList = useMemo(() => {
    return normalMemberList?.filter((item) => {
      if (isGroupOwner) {
        return item.role !== GroupPosition.Owner;
      } else {
        return item.role === GroupPosition.Member;
      }
    });
  }, [normalMemberList, isGroupOwner]);

  const data_ = useMemo(() => {
    return removeMemberList?.filter((item) => item.name?.includes(searchText));
  }, [searchText, removeMemberList]);

  const selectedContacts = normalMemberList.filter((item) =>
    selectUserIds.includes(item.user_id)
  );

  const hasMembers = normalMemberList.length > 0;

  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);

  const handleSelectAll = () => {
    if (selectUserIds.length === removeMemberList?.length) {
      setSelectUserIds([]);
    } else {
      setSelectUserIds(removeMemberList?.map((item) => item.user_id) ?? []);
    }
  };

  const onContactDelete = useCallback(
    (contact: ChatGroupMember) => {
      setSelectUserIds(
        selectedContacts.filter((c) => c.id !== contact.id).map((item) => item.user_id)
      );
    },
    [selectedContacts]
  );

  const handleDone = async () => {
    setLoading(true);
    await onActionOk?.(selectUserIds).catch((_) => '');
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setSelectUserIds([]);
      setSearchText('');
    }
  }, [open]);

  return (
    <Modal
      className={styles.groupRemoveModal}
      open={open}
      title="Remove Members"
      maskClosable={false}
      onCancel={onCancel}
      styles={{
        content: {
          padding: '24px 16px',
        },
      }}
      footer={
        <Flex justify={hasMembers ? 'space-between' : 'end'} align="center">
          {hasMembers && (
            <Checkbox
              className={styles.checkbox}
              indeterminate={
                selectedContacts.length > 0 &&
                selectedContacts.length < removeMemberList.length
              }
              checked={selectedContacts.length === removeMemberList.length}
              onChange={handleSelectAll}
            >
              Select all
            </Checkbox>
          )}
          <Flex gap={16} align="center" justify="end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              color="danger"
              variant="solid"
              loading={loading}
              onClick={handleDone}
              disabled={!selectedContacts.length}
            >
              Confirm
            </Button>
          </Flex>
        </Flex>
      }
    >
      <div className={styles.groupMembers}>
        <div className={styles.list}>
          <div className={styles.search}>
            <Input
              value={searchText}
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              variant="filled"
              placeholder="Search"
              onChange={onSearch}
            />
          </div>
          <div className={cls(styles.content, 'scrollbar_hover')}>
            <div className={styles.groupList}>
              {data_?.map((item) => {
                return (
                  <MemberItem
                    key={item.user_id}
                    name={item.name}
                    avatarProps={{ src: item.avatar, info: item }}
                    shortName={item.username}
                    checked={selectUserIds.includes(item.user_id)}
                    onClick={() => {
                      const newSelectUserIds = selectUserIds.includes(item.user_id)
                        ? selectUserIds.filter((id) => id !== item.user_id)
                        : [...selectUserIds, item.user_id];
                      setSelectUserIds(newSelectUserIds);
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className={styles.list}>
          <div className={styles.membersListTitle}>
            Selected: {selectUserIds.length}{' '}
            {selectUserIds.length > 1 ? 'Members' : 'Member'}
          </div>
          {selectUserIds.length > 0 && (
            <div className={cls(styles.selectedContactsListWrapper, 'scrollbar_hover')}>
              <div className={styles.selectedContactsList}>
                {selectedContacts.map((contact) => (
                  <Flex key={contact.id} align="center" justify="space-between">
                    <Flex align="center" justify="space-between" gap={10}>
                      <MyAvatar
                        src={getAvatar(contact.avatar, contact.name)}
                        info={contact}
                      />
                      <div className={styles.membersNames}>
                        <div className={styles.name}>{contact.name}</div>
                        {contact.username && (
                          <div className={styles.uname}>@{contact.username}</div>
                        )}
                      </div>
                    </Flex>
                    <DeleteIcon
                      className="pointer"
                      onClick={() => onContactDelete(contact)}
                    />
                  </Flex>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
export default GroupRemoveMembers;
